import React, { useEffect } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { styled } from '@mui/material/styles';
import { Button, Card, Grid, IconButton } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import ReactHookFormInput from 'common/components/form/ReactHookFormInput';
import yupResolver from 'common/utils/yupResolver';

import * as actions from './actions';
import * as selectors from './selectors';
import validate from './validate';

const RejectForm = styled('form')`
    align-items: center;
    background-color: rgba(0, 0, 0, 0.6);
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 2;

    .MuiTextField-root {
        border-radius: 20px;
    }
`;

const CardContainer = styled('div')`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: flex-end;
    max-width: 320px;
`;

const Title = styled('div')`
    color: #fa6968;
    font-size: 14px;
    font-weight: 600;
`;

const FormContainer = ({
    callback,
    chatId,
    documentId,
    documentType,
    handleOnReject,
    initialValues,
    isFetching,
    onChangeFileStatus,
    selectedFile,
    sendMessage,
    setShowModal,
    user,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    useEffect(() => reset(initialValues), [initialValues]);

    const onSubmit = (values) => {
        handleOnReject(selectedFile, () => {
            sendMessage(
                {
                    ...values,
                    chatId,
                    documentId,
                    documentType,
                    user,
                },
                () => {
                    setShowModal(false);
                    onChangeFileStatus();
                    reset({ message: '' }); // Limpiar el campo después de enviar
                    if (callback) callback();
                },
            );
        });
    };

    return (
        <RejectForm onSubmit={handleSubmit(onSubmit)}>
            <CardContainer>
                <Card>
                    <Card.Header>
                        <Grid container alignItems="center">
                            <Grid size={{ xs: 'grow' }}>
                                <Title>{t('Reason for rejection')}</Title>
                            </Grid>
                            <Grid>
                                <IconButton onClick={() => setShowModal(false)}>
                                    <CloseIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Card.Header>
                    <Card.Body>
                        <ReactHookFormInput
                            control={control}
                            name="message"
                            placeholder={t('Add comment')}
                            hasCounter
                            maxLength={255} // Límite consistente con otros formularios
                            multiline
                            fullWidth
                        />
                    </Card.Body>
                </Card>
            </CardContainer>
            <Button
                startIcon={<SendIcon />}
                disabled={isFetching}
                type="submit"
                variant="contained"
            >
                {t('Send reasons for rejection')}
            </Button>
        </RejectForm>
    );
};

FormContainer.propTypes = {
    callback: PropTypes.func,
    chatId: PropTypes.string,
    documentId: PropTypes.string,
    documentType: PropTypes.string,
    handleOnReject: PropTypes.func,
    initialValues: PropTypes.object,
    isFetching: PropTypes.bool,
    onChangeFileStatus: PropTypes.func,
    selectedFile: PropTypes.object,
    sendMessage: PropTypes.func,
    setShowModal: PropTypes.func,
    user: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
    isFetching: selectors.getSendIsFetching,
});

const mapDispatchToProps = (dispatch) => ({
    sendMessage: (values, callback) =>
        dispatch(actions.sendMessage(values, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FormContainer);
